import { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

/**
 * useStaplesData — owns the Spice Drawer's data lifecycle:
 *   • loads spices, others, and available regional presets
 *   • fires the once-per-mount "sync-empty" that promotes fully-drained
 *     spices into the grocery list
 *   • exposes the CRUD mutations the page needs (refill, setLevel,
 *     toggleStock, addCustom, loadPreset)
 *
 * The page keeps its own UI-only state (add dialog, puff / sparkle
 * animation IDs) — those are tightly coupled to the render tree.
 */
export default function useStaplesData() {
  const [spices, setSpices] = useState([]);
  const [others, setOthers] = useState([]);
  const [presets, setPresets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingPreset, setLoadingPreset] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, p] = await Promise.all([
        api.get("/staples"),
        api.get("/staples/presets"),
      ]);
      setSpices(s.data.spices || []);
      setOthers(s.data.others || []);
      setPresets(p.data.presets || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Fire-and-forget: any spice whose auto-drain has hit zero gets
  // promoted to out-of-stock and mirrored into the grocery upcoming list.
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.post("/staples/sync-empty");
        if (data.count > 0) {
          toast.info(`${data.count} spice${data.count === 1 ? "" : "s"} ran dry — added to your grocery list.`);
          load();
        }
      } catch (err) {
        console.debug("Staples sync-empty failed:", err);
      }
    })();
  }, [load]);

  const loadPreset = useCallback(async (key) => {
    setLoadingPreset(key);
    try {
      const { data } = await api.post("/staples/load-preset", { preset_key: key });
      if (data.added > 0) toast.success(`Added ${data.added} items to your Spice Drawer`);
      else toast.info("Everything's already on your shelf.");
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoadingPreset(null);
    }
  }, [load]);

  const refill = useCallback(async (id) => {
    try {
      const { data } = await api.post(`/staples/${id}/refill`);
      const cleared = data?.cleared_from_grocery > 0;
      if (cleared) {
        toast.success("Refilled — good as new.", {
          description: "Also cleared it from your grocery list.",
        });
      } else {
        toast.success("Refilled — good as new.");
      }
      await load();
      return true;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      return false;
    }
  }, [load]);

  const setLevel = useCallback(async (id, level) => {
    try {
      await api.put(`/staples/${id}/fill-level`, { level });
      toast.success("Noted.");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  }, [load]);

  const toggleStock = useCallback(async (id) => {
    try {
      const { data } = await api.post(`/staples/${id}/toggle-stock`);
      if (data.auto_added_to_grocery) {
        toast.success("Added to your grocery list.", {
          description: "It stays on the list until you tick it off there — even if you refill from the drawer.",
        });
      } else if (data.is_out_of_stock) {
        toast.info("Already on your grocery list.");
      } else {
        toast.success("Back in stock.");
      }
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  }, [load]);

  const addCustom = useCallback(async (form) => {
    const name = (form.name || "").trim();
    if (!name) return false;
    try {
      await api.post("/grocery/quick-add", {
        name,
        category: "Staples",
        status: "staple",
        staple_type: form.staple_type,
        typical_lifespan_days: Number(form.typical_lifespan_days) || 90,
        color: form.color,
        is_out_of_stock: false,
      });
      toast.success(`${name} added to the drawer`);
      load();
      return true;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      return false;
    }
  }, [load]);

  return {
    // data
    spices, others, presets, loading, loadingPreset,
    // handlers
    refill, setLevel, toggleStock, addCustom, loadPreset,
    load,
  };
}
